"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, useDragControls } from "framer-motion";
import dynamic from "next/dynamic";
import type { AiProfile } from "@/types/ai";
import BrandStyles from "@/components/branding/BrandStyles";
import { haptics } from "@/lib/utils/haptics";

const MindalooAppearancePanel = dynamic(
  () => import("@/components/karma-ac/MindalooAppearancePanel"),
  { ssr: false, loading: () => <PanelLoader label="Appearance" /> }
);

// Marketplace/Gallery/Projects/Vaults/Monitor/Effects/API-Keys/Security used
// to live here as their own SlidePanel destinations — every one of them was
// a decorative stand-in (a fake "Connect" toggle that connected nothing, a
// hardcoded "Online" status board, API key NAMES shown as read-only text
// with no way to actually enter one) reachable only from MindalooNavIsland,
// which turned out to not be mounted anywhere in the live app. Removed
// rather than left as unreachable dead weight; Settings already has a real
// API key manager, and the rest weren't backed by anything real to show.

const SettingsManager = dynamic(
  () => import("@/components/settings/SettingsManager"),
  { ssr: false, loading: () => <PanelLoader label="Settings" /> }
);
const MindalooAgentStudio = dynamic(
  () => import("@/components/karma-ac/MindalooAgentStudio"),
  { ssr: false, loading: () => <PanelLoader label="Studio" /> }
);

function PanelLoader({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 text-white/40">
      <div className="w-7 h-7 border-2 border-[#C1A2FF]/30 border-t-[#C1A2FF] rounded-full animate-spin" />
      <p className="text-xs font-light tracking-[0.2em] uppercase">{label}</p>
    </div>
  );
}

// "builder" (Consciousness Studio), "chat" (Thought Stream) and "multichat"
// (Multi-Agent) used to live here as tabs — a second, weaker reimplementation
// of things that already work for real on the main canvas: personality/
// identity editing lives in the Studio ("finetune" below) — a real, full
// customizer (identity, personality, appearance, behavior, worker roster)
// instead of a stub. Multi-agent sessions are just opening 2+ tiles and
// linking them via SpatialCommandCenter, not a separate modal.
export type SlidePanel =
  | "settings"
  | "appearance"
  | "finetune"
  | null;

const PANEL_META: Record<string, { label: string; icon: string; desc: string }> = {
  settings:    { label: "Settings",             icon: "⚙️",  desc: "Configuration & runtime" },
  appearance:  { label: "Appearance",            icon: "🎨", desc: "Themes & visual style" },
  finetune:    { label: "Studio",                icon: "✨", desc: "Identity, personality & appearance" },
};

interface MindalooSlideOverProps {
  panel: SlidePanel;
  profile: AiProfile | null;
  profiles?: AiProfile[];
  onClose: () => void;
  onProfileUpdate?: (profile: AiProfile) => void;
}

export default function MindalooSlideOver({
  panel,
  profile,
  profiles = [],
  onClose,
  onProfileUpdate,
}: MindalooSlideOverProps) {
  const [activeTab, setActiveTab] = useState<SlidePanel>(panel);
  const dragY = useMotionValue(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();

  const accent = profile?.brand?.primaryColor ?? "#C1A2FF";
  const secondary = profile?.brand?.secondaryColor ?? "#9333EA";

  // Sync activeTab + reset drag when panel opens/changes
  useEffect(() => {
    if (panel) {
      setActiveTab(panel);
      dragY.set(0);
    }
  }, [panel, dragY]);

  const handleDragEnd = useCallback((_: any, info: { offset: { y: number }; velocity: { y: number } }) => {
    const { offset, velocity } = info;
    if (offset.y > 120 || velocity.y > 500) {
      haptics.click();
      onClose();
    } else {
      dragY.set(0);
    }
  }, [onClose, dragY]);

  const backdropOpacity = useTransform(dragY, [0, 200], [1, 0]);
  const panelScale = useTransform(dragY, [0, 300], [1, 0.97]);
  const panelBorderRadius = useTransform(dragY, [0, 100], [28, 40]);

  return (
    <AnimatePresence>
      {panel && profile && (
        <>
          {/* Backdrop */}
          <motion.div
            key="slideover-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ opacity: backdropOpacity }}
            // .karma-chatbar-wrap (the persistent bottom bar) sits at
            // z-index 10000 so it stays above tiles/canvas content — this
            // panel was at 1600/1610, well below that, so the bottom bar's
            // Atlas strip and tile chips always rendered on top of Settings,
            // visibly clashing with its bottom-most content. A modal panel
            // should outrank persistent chrome when it's open.
            className="fixed inset-0 z-[10600] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            key="slideover-panel"
            ref={panelRef}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.28 }}
            dragMomentum={false}
            onDragEnd={handleDragEnd}
            style={{ y: dragY, scale: panelScale, borderTopLeftRadius: panelBorderRadius, borderTopRightRadius: panelBorderRadius, top: "max(32px, 5dvh)" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%", transition: { type: "spring", stiffness: 400, damping: 38 } }}
            transition={{ type: "spring", stiffness: 360, damping: 34 }}
            className="slide-panel fixed inset-x-0 bottom-0 z-[10610] overflow-hidden flex flex-col"
          >
            <BrandStyles brand={profile.brand} />

            {/* Accent gradient cap */}
            <div
              className="slide-panel-cap shrink-0"
              style={{ background: `linear-gradient(135deg, ${accent}22 0%, ${secondary}18 100%)` }}
            >
              {/* Drag pill — pointer events here start drag on the panel */}
              <div
                className="slide-panel-handle cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => dragControls.start(e)}
              />

              {/* Profile row */}
              <div className="flex items-center gap-3 px-5 pt-1 pb-3">
                <div
                  className="slide-panel-avatar"
                  style={{ background: `linear-gradient(135deg, ${accent}, ${secondary})` }}
                >
                  {profile.name?.[0]?.toUpperCase() ?? "M"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white/90 truncate">{profile.name}</p>
                  <p className="text-[10px] text-white/40 truncate">
                    {activeTab ? (PANEL_META[activeTab]?.desc ?? "") : ""}
                  </p>
                </div>
                <motion.button
                  type="button"
                  onClick={onClose}
                  className="slide-panel-close"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Close"
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </motion.button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-hidden slide-panel-body">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab ?? "none"}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="h-full"
                >
                  {activeTab === "settings" && (
                    <SettingsManager profile={profile!} onProfileUpdate={onProfileUpdate ?? (() => {})} />
                  )}
                  {activeTab === "appearance" && <MindalooAppearancePanel open profile={profile!} onClose={onClose} onProfileUpdate={(p) => onProfileUpdate?.(p)} />}
                  {activeTab === "finetune" && (
                    <MindalooAgentStudio
                      profile={profile!}
                      profiles={profiles}
                      onProfileUpdate={onProfileUpdate ?? (() => {})}
                      onClose={onClose}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
